import type { Session } from '@/types';

export type SessionStatusTone = 'attended' | 'cancelled';

export interface SessionStatusPill {
  label: string;
  tone: SessionStatusTone;
}

const ACTIONABLE_SESSION_STATUSES = new Set([
  'active',
  'pending',
  'rescheduled',
]);

export const isSessionActionable = (session: Session): boolean =>
  ACTIONABLE_SESSION_STATUSES.has(session.status);

// MEN-5338: a Rails-owned Moments booking (series or chemistry) with no native
// Appointment behind it. Ops acts on these through the Rails
// /admin/calendar/sessions routes, which accept exactly `moments_booking?`
// sessions. Rails exposes `source_appointment_id` for native-derived and
// migration-rebound sessions, which it refuses here, so that excludes them.
export const isAdminLegacyMomentsSession = (session: Session): boolean =>
  !session.sourceAppointmentId &&
  session.bookingSource === 'moments' &&
  Boolean(session.momentsBookingId);

const RESCHEDULE_NOTICE_MS = 48 * 60 * 60 * 1000;

const isAdminChemistryActionable = (
  session: Session,
  adminMode: boolean,
): boolean =>
  adminMode &&
  session.bookingSource === 'moments' &&
  session.isChemistryCall &&
  !session.recurringId &&
  Boolean(session.momentsBookingId) &&
  session.bookId === `moments:${session.momentsBookingId}` &&
  new Date(session.startedAt).getTime() > Date.now() &&
  isSessionActionable(session);

export const canRescheduleSession = (
  session: Session,
  adminMode = false,
): boolean => {
  // MEN-4695 (review finding, reproduced): the native Appointment's
  // actionCapabilities are the authority for reschedule affordances.
  // When the click adapter carries them through as
  // `sourceAppointmentRescheduleAllowed`, respect them BEFORE any
  // legacy-mode branch — otherwise the admin chemistry branch would
  // short-circuit and re-enable a control the Appointment has denied.
  //
  // The 48h RESCHEDULE_NOTICE_MS floor is NOT applied here: the
  // backend Appointment already encodes admin chemistry's near-term
  // block via `actionCapabilities.reschedule.allowed` (see
  // moments/backend/pkg/appointment/rpc_http_handler.go — admin
  // capabilities zero the scopes inside the 48h window). Coach and
  // member coaching paths historically treat 48h as a soft warning
  // in the reschedule dialog, not a hard gate on the affordance;
  // applying a client-side 48h block here would newly disable
  // reschedule for those paths. Trust the Appointment authority.
  if (session.sourceAppointmentRescheduleAllowed !== undefined) {
    return session.sourceAppointmentRescheduleAllowed;
  }
  if (adminMode && session.isChemistryCall) {
    return (
      isAdminChemistryActionable(session, adminMode) &&
      new Date(session.startedAt).getTime() > Date.now() + RESCHEDULE_NOTICE_MS
    );
  }
  return session.coachRescheduleAvailable ?? isSessionActionable(session);
};

export const canCancelSession = (
  session: Session,
  adminMode = false,
): boolean => {
  // MEN-4695 (review finding): same authority precedence as
  // canRescheduleSession — Appointment's actionCapabilities beat the
  // admin chemistry branch.
  if (session.sourceAppointmentCancelAllowed !== undefined) {
    return session.sourceAppointmentCancelAllowed;
  }
  if (adminMode && session.isChemistryCall) {
    return isAdminChemistryActionable(session, adminMode);
  }
  return session.coachCancelAvailable ?? isSessionActionable(session);
};

// The coach sessions API returns sessions grouped by "<month>-<year>". The List
// view needs a single start-sorted stream, so flatten and sort by startedAt.
export const flattenSessionGroups = (
  groups: Record<string, Session[]> | undefined,
): Session[] => {
  if (!groups) return [];

  return Object.values(groups)
    .flat()
    .sort(
      (left, right) =>
        new Date(left.startedAt).getTime() -
        new Date(right.startedAt).getTime(),
    );
};

// Derives the session status pill (MEN-4160). Cancelled -> attributed to the
// actor when known, past or future (MEN-5300: List view keeps cancelled
// records, so a future one without a pill reads as an active upcoming
// session). Past done -> "Attended"; other upcoming/active sessions get none.
export const deriveSessionStatus = (
  session: Session,
): SessionStatusPill | null => {
  if (session.status === 'cancelled') {
    if (session.cancelledBy === 'member') {
      return { label: 'Cancelled by Member', tone: 'cancelled' };
    }
    if (session.cancelledBy === 'coach') {
      return { label: 'Cancelled by Coach', tone: 'cancelled' };
    }
    return { label: 'Cancelled', tone: 'cancelled' };
  }

  if (new Date(session.endedAt).getTime() >= Date.now()) return null;

  if (session.status === 'done') {
    return { label: 'Attended', tone: 'attended' };
  }

  return null;
};
